import Box from '@mui/material/Box';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Stack from '@mui/material/Stack';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import SearchFilters from './SearchFilters';
import './Search.css';
import { useAppContext } from '../Store';
import { SET_PREVIOUS_VIEWDOC_ID, setSearchQuery, setSearchScope } from '../reducer';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';

export const filterByOptions = [
  { label: 'Form Number', value: 'formNumber', selected: false },
  { label: 'Title', value: 'formTitle', selected: false },
  { label: 'All', value: '', selected: true },
];

const Search = () => {
  const { state, dispatch } = useAppContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(inputRef.current?.value || state.search.searchQuery);
  const [narrow, setNarrow] = useState<string | undefined>(state.search.narrow);
  const [searchValue] = useDebounce(value, 1500);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const getSearchResults = useCallback(async () => {
    dispatch(setSearchQuery(searchValue));
    dispatch({ type: SET_PREVIOUS_VIEWDOC_ID, payload: '' });
  }, [searchValue, dispatch]);

  const debouncedGetSearchResults = useDebouncedCallback(getSearchResults, 750);
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === '/search') {
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  useEffect(() => {
    if (searchValue !== state.search.searchQuery) {
      getSearchResults();
    }
  }, [searchValue, state.search.searchQuery, getSearchResults]);

  useEffect(() => {
    if (state.search.narrow !== narrow) {
      setNarrow(state.search.narrow);
      if (searchValue.length > 0) {
        debouncedGetSearchResults();
      }
    }
  }, [state.search.narrow, narrow, searchValue.length, debouncedGetSearchResults]);

  const search = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setValue(query);
    if (inputRef.current) {
      inputRef.current.value = query;
    }
  };

  return (
    <Box className="section-full" sx={{ flexDirection: 'column' }}>
      <Stack sx={{ width: 1, px: 4, pb: 3 }}>
        <Stack className="container wrapper">
          <Stack
            direction="column"
            gap={1}
            alignItems="center"
            justifyContent="center"
            sx={{
              width: '75%',
              pb: 2,
              ml: 'auto',
              mr: 'auto',
            }}
          >
            <Stack style={{ width: '65%' }} justifyContent={'center'} gap={1}>
              <FormControl>
                <Box display="flex" alignItems="center" alignContent={'center'}>
                  <FormLabel
                    sx={{ ml: 0.25, mr: 1, fontWeight: 600, fontSize: '1rem' }}
                    focused
                    color="primary"
                  >
                    Search By:
                  </FormLabel>
                  <RadioGroup
                    row
                    value={state.search.narrow}
                    onChange={(e) => {
                      if (!state.isLoading) dispatch(setSearchScope(e.target.value));
                    }}
                  >
                    {[
                      { value: 'formNumber', label: 'Form Number' },
                      { value: 'formTitle', label: 'Form Title' },
                      { value: '', label: 'All' },
                    ].map((obj, index) => (
                      <FormControlLabel
                        key={index}
                        value={obj.value}
                        control={<Radio size="small" />}
                        label={obj.label}
                        sx={{
                          '.MuiFormControlLabel-label': {
                            fontSize: '0.8rem',
                          },
                          my: 0,
                          mx: 0.25,
                          '.MuiRadio-root': {
                            p: 0,
                          },
                        }}
                      />
                    ))}
                  </RadioGroup>
                </Box>
              </FormControl>
              <Stack direction={'row'} alignItems={'center'}>
                <TextField
                  type="search"
                  sx={{
                    flex: 1,
                    '.MuiInputBase-root': {
                      pl: 1,
                    },
                  }}
                  ref={inputRef}
                  value={value}
                  onChange={search}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" disablePointerEvents>
                        <SearchIcon color="primary" />
                      </InputAdornment>
                    ),
                    sx: {
                      '& .MuiInputBase-input': {
                        py: 0.75,
                      },
                    },
                  }}
                />
                <IconButton onClick={() => setDialogOpen(true)}>
                  <InfoOutlined sx={{ color: '#003865' }} />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
          <SearchFilters />
        </Stack>
        {dialogOpen && (
          <Dialog maxWidth={'tablet'} onClose={() => setDialogOpen(false)} open={dialogOpen}>
            <Stack flexDirection={'row'} alignItems={'center'}>
              <DialogTitle>Search Help</DialogTitle>
              <div style={{ marginLeft: 'auto', paddingRight: '24px' }}>
                <IconButton aria-label="close" onClick={() => setDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </div>
            </Stack>
            <DialogContent dividers>
              <Typography gutterBottom>
                • Search by <strong>Form Number</strong> or <strong>Form Title</strong> for the most
                direct results
              </Typography>
              <Typography gutterBottom>
                • Search by <strong>All</strong> to look for specific words or text within a form
              </Typography>
              <Typography gutterBottom>
                • Wrap your search in <strong>"quotes"</strong> to find{' '}
                <strong>exact phrases</strong> — e.g. <em>"Reported Policy"</em>
              </Typography>
              <Typography gutterBottom>
                • Users can search for forms based on their status -{' '}
                <strong>Active, Inactive, or Both</strong> - by selecting the appropriate checkboxes
              </Typography>
              <Typography gutterBottom>
                • Apply filters like <strong>Business Unit &gt; Sub Business Unit</strong> and{' '}
                <strong>Line of Business</strong> to tailor results to your business
              </Typography>
              <Typography gutterBottom>
                • Select <strong>Show Advanced Filters</strong> to display additional ways to filter
                the search results.
              </Typography>
              <Typography gutterBottom>
                • <strong>Save</strong> and <strong>reuse filters</strong> for faster future
                searches
              </Typography>
            </DialogContent>
          </Dialog>
        )}
      </Stack>
    </Box>
  );
};

export default Search;
